import { 
  Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, 
  inject, signal, computed, effect 
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserDataService } from '../services/user-data.service';
import { BubblePhysicsService } from '../services/bubble-physics.service';
import { PerformanceMonitorService } from '../services/performance-monitor.service';
import { ParamParser } from '../utils/param-parser';
import { Bubble } from '../models/bubble';
import { ChatUser } from '../models/user';
import { firstValueFrom } from 'rxjs';

interface AppSettings {
  maxBubbles: number;
  gravity: number;
  minSpeed: number;
  startSpeed: number;
  maxSpeed: number;
  friction: number;
  bubbleRadius: number;
  greetingCooldown: number;
  showGreeting: boolean;
  floatMode: boolean;
  defaultImg: string;
  allowDefaultImg: boolean;
  showUsername: boolean;
  ceiling: number;
  collision: boolean;
  randomHop: boolean;
  randomSwitch: boolean;
  maxBubblesCollision: number;
  bounceFloor: boolean;
  headPiece: boolean | string;
  slideTrough: boolean;
  testMode: boolean;
  vipOnly: boolean;
  showEvents: boolean;
  preset: string;
  [key: string]: any; // Добавьте индексную сигнатуру
}

@Component({
  selector: 'app-bubble-overlay',
  templateUrl: './bubble-overlay.component.html',
  styleUrls: ['./bubble-overlay.component.scss']
})
export class BubbleOverlayComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('bubbleContainer', { static: true }) bubbleContainer!: ElementRef;

  private userDataService = inject(UserDataService);
  private physicsService = inject(BubblePhysicsService);
  private performanceMonitor = inject(PerformanceMonitorService);
  private route = inject(ActivatedRoute);

  // Сигналы состояния
  private animationFrameId = signal<number>(0);
  private userFetchInterval = signal<ReturnType<typeof setInterval> | null>(null);
  private memoryCleanupInterval = signal<ReturnType<typeof setInterval> | null>(null);
  settings = signal<any>({});
  bubbles: Bubble[] = [];
  chatUsers = signal<ChatUser[]>([]);
  private lastFetchTime = signal<number>(0);
  private frameCount = 0;
  private lastCleanup = Date.now();

  // Вычисляемые сигналы
  activeBubblesCount = computed(() => this.bubbles.length);
  isTestMode = computed(() => this.settings().testMode ?? true);
  shouldFetchUsers = computed(() => Date.now() - this.lastFetchTime() > 9000);

  // Настройки по умолчанию
  private defaultSettings: AppSettings = {
    maxBubbles: 200,
    gravity: 0,
    minSpeed: 0,
    startSpeed: 0.75,
    maxSpeed: 0.5,
    friction: 1,
    bubbleRadius: 25,
    greetingCooldown: 300000,
    showGreeting: true,
    floatMode: true,
    defaultImg: '/assets/icons/x.svg',
    allowDefaultImg: false,
    showUsername: true,
    ceiling: 0,
    collision: true,
    randomHop: true,

    randomSwitch: false,
    maxBubblesCollision: 100,
    bounceFloor: true,
    headPiece: false,
    slideTrough: true,
    testMode: false,
    vipOnly: false,
    showEvents: false,
    preset: 'floating'
  };

  // Эффект: следим за обновлениями пользователей из сервиса (сигнал)
  private syncUsersEffect = effect(() => {
    const users = this.userDataService.getChatUsers()(); // подписка на сигнал из сервиса
    this.chatUsers.set(users);
  });

  // Эффект: обработка изменений пользователей
  private userProcessingEffect = effect(() => {
    const users = this.chatUsers();
    this.processChatUsers(users, this.settings());
  });

  constructor() {}

  ngOnInit() {
    this.parseQueryParams();
  }

  ngAfterViewInit() {
    this.startAnimation();
    this.fetchChatUsers();      // Немедленный запрос
    this.startUserFetching();   // Периодический опрос
    this.startMemoryCleanup();  // Очистка памяти
    this.performanceMonitor.startMonitoring(); // Мониторинг производительности
  }

  ngOnDestroy() {
    this.stopAnimation();
    this.stopUserFetching();
    this.stopMemoryCleanup();
    this.cleanupBubbles();
    this.physicsService.cleanup();
    this.performanceMonitor.stopMonitoring();
  }

 private parseQueryParams(): void {
    this.route.queryParamMap.subscribe(params => {
      const urlParams = new URLSearchParams();
      params.keys.forEach(key => urlParams.set(key, params.get(key) || ''));

      const parsedParams = ParamParser.parseParams(urlParams);
      this.settings.set(parsedParams);
      this.applyPresetSettings();
      this.initializeSettings(); // ← Вызываем здесь после применения preset
    });
  }

    private applyPresetSettings(): void {
    const currentSettings = this.settings();
    const preset = currentSettings['preset'] || this.defaultSettings.preset;

    const updatedSettings = { ...currentSettings };
    ParamParser.applyPreset(updatedSettings, preset);
    this.settings.set(updatedSettings);
  }

  private initializeSettings(): void {
    const currentSettings = this.settings();
    
    // Объединяем настройки, но НЕ перезаписываем preset значения
    const mergedSettings = { ...this.defaultSettings };
    
    // Перезаписываем default значения только теми, что не установлены в preset
    Object.keys(currentSettings).forEach(key => {
      if (currentSettings[key] !== undefined) {
        mergedSettings[key] = currentSettings[key];
      }
    });

    this.settings.set(mergedSettings);

    document.documentElement.style.setProperty(
      '--bubble-radius',
      `${mergedSettings.bubbleRadius}px`
    );
  }


  private startAnimation(): void {
    const animate = () => {
      this.frameCount++;
      
      if (this.bubbles.length > 0) {
        this.updatePhysics();
        this.updateBubblePositions();
      }
      
      // Периодическая очистка каждые 30 секунд
      if (this.frameCount % 1800 === 0) {
        this.performMemoryCleanup();
      }
      
      this.animationFrameId.set(requestAnimationFrame(animate));
    };
    this.animationFrameId.set(requestAnimationFrame(animate));
  }

  private stopAnimation(): void {
    const frameId = this.animationFrameId();
    if (frameId) cancelAnimationFrame(frameId);
    this.animationFrameId.set(0);
  }

  private startUserFetching(): void {
    const interval = setInterval(() => this.fetchChatUsers(), 30000);
    this.userFetchInterval.set(interval);
  }

  private stopUserFetching(): void {
    const interval = this.userFetchInterval();
    if (interval) clearInterval(interval);
    this.userFetchInterval.set(null);
  }

  private startMemoryCleanup(): void {
    const interval = setInterval(() => this.performMemoryCleanup(), 30000);
    this.memoryCleanupInterval.set(interval);
  }

  private stopMemoryCleanup(): void {
    const interval = this.memoryCleanupInterval();
    if (interval) clearInterval(interval);
    this.memoryCleanupInterval.set(null);
  }

  private performMemoryCleanup(): void {
    const now = Date.now();
    if (now - this.lastCleanup < 25000) return;
    
    // Принудительная сборка мусора при низком FPS
    const fps = this.performanceMonitor.getFPS();
    if (fps < 40) {
      this.performanceMonitor.forceGarbageCollection();
    }
    
    // Принудительная очистка DOM для пузырьков
    const container = this.bubbleContainer.nativeElement;
    const orphanedElements = container.querySelectorAll('.bubble');
    const activeBubbleElements = new Set(this.bubbles.map(b => b.element));
    
    orphanedElements.forEach((el: Element) => {
      if (!activeBubbleElements.has(el as HTMLElement)) {
        el.remove();
      }
    });
    
    // Ограничиваем количество пузырьков при длительной работе
    const maxBubbles = this.settings().maxBubbles || 200;
    if (this.bubbles.length > maxBubbles * 0.8) {
      const toRemove = this.bubbles.slice(0, Math.floor(this.bubbles.length * 0.1));
      this.removeBubbles(toRemove);
    }
    
    this.lastCleanup = now;
    const memUsage = this.performanceMonitor.getMemoryUsage();
    console.log(`🧹 Memory cleanup: ${this.bubbles.length} bubbles, ${fps} fps, ${memUsage} MB`);
  }

  /** Загружаем пользователей через сервис */
  private async fetchChatUsers(): Promise<void> {
    try {
      const settings = this.settings();
      let users: ChatUser[];

      if (settings.testMode) {
        users = this.userDataService.generateTestUsers(10);
      } else {
        users = await firstValueFrom(this.userDataService.fetchChatUsers());
      }

      this.chatUsers.set(users);
      this.lastFetchTime.set(Date.now());
    } catch (err) {
      console.error('Error fetching chat users:', err);
      this.chatUsers.set(this.userDataService.generateTestUsers(5));
      this.lastFetchTime.set(Date.now());
    }
  }

// Вспомогательные методы для генерации уникального ключа
private getUserKey(user: ChatUser): string {
  // Если это дефолтный аватар — ключ только по имени
  if (user.avatar.includes('default.png')) {
    return `username:${user.username}`;
  }
  return `avatar:${user.avatar}|username:${user.username}`;
}

private getBubbleKey(bubble: Bubble): string {
  if (bubble.avatar.includes('default.png')) {
    return `username:${bubble.username}`;
  }
  return `avatar:${bubble.avatar}|username:${bubble.username}`;
}

 private createBubbleElement(user: ChatUser, settings: any): HTMLElement {
    const bubble = document.createElement('div');
    bubble.className = 'bubble';
    
    // Используем CSS переменные вместо inline стилей для лучшей производительности
    bubble.style.cssText = `
      position: absolute;
      width: ${settings.bubbleRadius * 2}px;
      height: ${settings.bubbleRadius * 2}px;
    `;

    let borderColor = 'transparent';

    if (user.is_discord_vip) borderColor = '#5765F2';
    if (user.is_youtube_member) borderColor = '#FF0032';
    if (user.is_nft_holder) borderColor = '#9047FF';

    // Создаем контейнер для градиентной "границы"
    const gradientBorder = document.createElement('div');
    gradientBorder.style.cssText = `
      width: 100%;
      height: 100%;
      border-radius: 50%;
      padding: 4px;
      box-sizing: border-box;
    `;

    if (user.is_discord_vip && user.is_youtube_member && user.is_nft_holder) {
        gradientBorder.style.background = 'radial-gradient(circle, #f59823 92%, #ffbc39 95%, #ffd9c9 97%, #ffbc39 98%, #f59823 100%)';
    } else {
        gradientBorder.style.background = borderColor;
    }

    // Внутренний контейнер для изображения
    const imgContainer = document.createElement('div');
    imgContainer.style.cssText = `
      width: 100%;
      height: 100%;
      border-radius: 50%;
      overflow: hidden;
    `;

    const img = document.createElement('img');
    img.src = user.avatar;
    img.alt = user.username;
    img.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: cover;
    `;
    
    // Добавляем обработчик ошибок для изображений
    img.onerror = () => {
      img.src = settings.defaultImg || '/assets/icons/x.svg';
    };

    imgContainer.appendChild(img);
    gradientBorder.appendChild(imgContainer);
    bubble.appendChild(gradientBorder);

    if (settings.showUsername) {
        const label = document.createElement('div');
        label.className = 'username-label';
        label.textContent = user.username;
        label.style.cssText = `
          position: absolute;
          bottom: 110%;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0,0,0,0.7);
          color: white;
          padding: 3px 6px;
          font-size: 12px;
          border-radius: 5px;
          white-space: nowrap;
          pointer-events: none;
        `;
        bubble.appendChild(label);
    }

    // Корону добавляем только для пользователей без статусов
    if (user.is_discord_vip && user.is_youtube_member && user.is_nft_holder) {
        let crown = document.createElement('div');
        crown.classList.add('crown');
        crown.style.cssText = `
          position: absolute;
          top: -50px;
          left: 55%;
          transform: translateX(-50%);
          font-size: 18px;
          color: gold;
          pointer-events: none;
        `;
        crown.innerHTML = '👑';
        bubble.appendChild(crown);
    }

    return bubble;
}

  private createBubble(user: ChatUser, element: HTMLElement, settings: any): Bubble {
  const containerWidth = this.bubbleContainer.nativeElement.clientWidth;
  const containerHeight = this.bubbleContainer.nativeElement.clientHeight;
  
  const bubble: Bubble = {
    element,
    x: Math.random() * (containerWidth - settings.bubbleRadius * 2),
    y: this.getRandomYPosition(containerHeight, settings),
    vx: this.randomSpeed(settings), // Используйте randomSpeed
    vy: this.randomSpeed(settings), // Используйте randomSpeed
    radius: settings.bubbleRadius,
    userId: user.id,
    username: user.username,
    avatar: user.avatar,
    isDiscordVip: user.is_discord_vip,
    isYoutubeMember: user.is_youtube_member,
    youtubeMembershipLevel: user.youtube_membership_level,
    isMeeting: false, // Измените на false для движения
    isPaused: false,
    isHopping: true,
    hasJumpedToSpaceship: false,
    isJumpingToSpaceship: false
  };

  // element.style.left = `${bubble.x}px`;
  // element.style.top = `${bubble.y}px`;
  return bubble;
}

 private getRandomYPosition(containerHeight: number, settings: any): number {
    const ceiling = settings.ceiling;
    if (ceiling === 0) {
      
      return Math.random() * (containerHeight - settings.bubbleRadius * 2);
    }
    return this.randomInRange(
      containerHeight - ceiling + settings.bubbleRadius * 3,
      containerHeight - settings.bubbleRadius * 3
    );
}


  private randomInRange(min: number, max: number): number {
    return Math.random() * (max - min) + min;
  }

  private cleanupBubbles(): void {
    this.bubbles.forEach(b => b.element.remove());
    this.bubbles = [];
    this.chatUsers.set([]);
  }

  private randomSpeed(settings: any): number {
  if (settings.maxSpeed <= 0) return 1;
  if (settings.maxSpeed === settings.minSpeed) return settings.maxSpeed;

  const magnitude = Math.random() * (settings.maxSpeed - settings.minSpeed) + settings.minSpeed;
  const direction = Math.random() < 0.5 ? -1 : 1;
  return magnitude * direction;
}


private processChatUsers(users: ChatUser[], settings: any): void {
  const currentBubbles = this.bubbles;
  
  // Создаем Set для идентификации уникальных пользователей по avatar + username
  const activeUserKeys = new Set(users.map(u => this.getUserKey(u)));
  const currentUserKeys = new Set(currentBubbles.map(b => this.getBubbleKey(b)));

  // Сначала удаляем пузырьки для пользователей, которых больше нет
  const bubblesToRemove = currentBubbles.filter(b => 
    !activeUserKeys.has(this.getBubbleKey(b))
  );
  
  if (bubblesToRemove.length > 0) {
    this.removeBubbles(bubblesToRemove);
  }

  // Добавляем новых пользователей только если есть место
  const remainingBubbles = this.bubbles;
  if (remainingBubbles.length < settings.maxBubbles) {
    const remainingUserKeys = new Set(remainingBubbles.map(b => this.getBubbleKey(b)));
    const toAdd = users.filter(u => 
      !remainingUserKeys.has(this.getUserKey(u))
    ).slice(0, settings.maxBubbles - remainingBubbles.length);
    
    if (toAdd.length > 0) {
      this.addUsersAsBubbles(toAdd, settings);
    }
  }
}

private removeBubbles(toRemove: Bubble[]): void {
  if (toRemove.length === 0) return;

  toRemove.forEach(b => {
    // Очищаем таймеры для этого пузырька
    this.physicsService.clearBubbleTimers(b);
    
    // Удаляем DOM элемент
    if (b.element && b.element.parentNode) {
      b.element.remove();
    }
    
    // Удаляем из массива
    const index = this.bubbles.indexOf(b);
    if (index !== -1) this.bubbles.splice(index, 1);
  });
}

private addUsersAsBubbles(users: ChatUser[], settings: any): void {
  const container = this.bubbleContainer.nativeElement;

  users.forEach(user => {
    if (this.bubbles.length >= settings.maxBubbles) return;

    const bubbleElement = this.createBubbleElement(user, settings);
    const bubble = this.createBubble(user, bubbleElement, settings);

    this.bubbles.push(bubble);          // мутируем массив
    container.prepend(bubbleElement);   // добавляем DOM элемент
  });
}


private updatePhysics(): void {
  if (this.bubbles.length === 0) return;

  this.physicsService.updatePhysics(
    this.bubbles,
    this.bubbleContainer.nativeElement.clientWidth,
    this.bubbleContainer.nativeElement.clientHeight,
    this.settings()
  );
  // ⚡️ сигнал не обновляем, массив мутирован
}

private updateBubblePositions(): void {
  // Батчинг DOM операций
  const updates: Array<{element: HTMLElement, x: number, y: number}> = [];
  
  this.bubbles.forEach(b => {
    if (b.element) {
      updates.push({element: b.element, x: b.x, y: b.y});
    }
  });
  
  // Применяем все изменения за один раз
  requestAnimationFrame(() => {
    updates.forEach(({element, x, y}) => {
      element.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    });
  });
}
}
